import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import Modal from "@/components/base/Modal";
import Avatar from "@/components/base/Avatar";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import { apiPost } from "@/lib/api";
import type { LadderDetail, LadderPrize, LadderPlayResult } from "@/lib/types";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";
import LadderBoard from "../components/LadderBoard";
import ResultModal from "../components/ResultModal";
import { STATE_LABEL, formatCountdown, formatDateTime } from "../utils";

interface PlayState {
  slot: number;
  end: number;
  prize: LadderPrize | null;
  rungs: [number, number][] | null;
  ends: number[] | null;
}

export default function LadderDetailPage() {
  const { no } = useParams();
  const navigate = useNavigate();
  const { requireLogin } = useAuth();
  const { toast } = useToast();
  const now = useNow();

  const { data, loading, error, reload, serverTs } = useApi<LadderDetail>(
    no ? `ladders.php?no=${no}` : null
  );

  const [confirmSlot, setConfirmSlot] = useState<number | null>(null);
  const [joining, setJoining] = useState(false);
  const [playState, setPlayState] = useState<PlayState | null>(null);
  const [result, setResult] = useState<{
    prize: LadderPrize | null;
    end: number;
  } | null>(null);

  const taken = useMemo(
    () =>
      (data?.slots ?? [])
        .filter((s) => s.taken === 1)
        .map((s) => s.slot),
    [data]
  );
  const joinedSlots = useMemo(
    () => (data?.slots ?? []).filter((s) => s.taken === 1),
    [data]
  );

  const mySlot = data?.my?.slot ?? null;
  const isOpen = data?.state === "open";

  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const endRemaining = Math.max(0, (data?.end_in ?? 0) - elapsed);
  const startRemaining = Math.max(0, (data?.start_in ?? 0) - elapsed);

  // 참여 즉시공개 응답이 오면 그 rungs/ends 로, 아니면 상세 데이터로
  const activeRungs = playState ? playState.rungs : (data?.rungs ?? null);
  const activeEnds = playState ? playState.ends : (data?.ends ?? null);
  const activeReveal: 0 | 1 = playState ? 1 : (data?.reveal ?? 0);
  const animateSlot = playState ? playState.slot : null;

  const handleSlotClick = (slot: number) => {
    if (!isOpen) return;
    if (mySlot != null) return;
    if (taken.includes(slot)) return;
    if (!requireLogin()) return;
    setConfirmSlot(slot);
  };

  const handleConfirm = useCallback(async () => {
    if (confirmSlot == null || !no) return;
    setJoining(true);
    try {
      const res = await apiPost<LadderPlayResult>("ladder_play.php", {
        no: Number(no),
        slot: confirmSlot,
      });
      const d = res.data;
      setConfirmSlot(null);
      // 즉시공개: rungs 가 함께 내려옴 → 애니메이션
      if (d && d.rungs) {
        setPlayState({
          slot: confirmSlot,
          end: d.end ?? confirmSlot,
          prize: d.prize ?? null,
          rungs: d.rungs,
          ends: d.ends ?? null,
        });
      } else {
        // 종료 후 공개: 메시지만 표시
        toast(res.msg || "참여했습니다.", "success");
        reload();
      }
    } catch (e) {
      setConfirmSlot(null);
      toast(e instanceof Error ? e.message : "참여에 실패했습니다.", "error");
      reload();
    } finally {
      setJoining(false);
    }
  }, [confirmSlot, no, toast, reload]);

  const handleAnimateEnd = useCallback(() => {
    if (!playState) return;
    setResult({ prize: playState.prize, end: playState.end });
    setPlayState(null);
    reload();
  }, [playState, reload]);

  if (loading) {
    return (
      <>
        <PageBanner
          title="사다리게임"
          breadcrumb={[{ label: "사다리게임", to: "/ladder" }, { label: "상세" }]}
        />
        <div className="mx-auto max-w-5xl px-4 md:px-6 py-10 space-y-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-72 w-full" />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageBanner
          title="사다리게임"
          breadcrumb={[{ label: "사다리게임", to: "/ladder" }, { label: "상세" }]}
        />
        <section className="mx-auto max-w-5xl px-4 md:px-6 py-12">
          <EmptyState
            icon="ri-error-warning-line"
            title="게임을 불러오지 못했습니다"
            description={error ?? "존재하지 않는 게임입니다."}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        </section>
      </>
    );
  }

  return (
    <>
      <PageBanner
        title="사다리게임"
        breadcrumb={[{ label: "사다리게임", to: "/ladder" }, { label: data.subject || "상세" }]}
      />

      <section className="mx-auto max-w-5xl px-4 md:px-6 py-8 md:py-10">
        {/* 제목 + 상태 */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-block rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-900">
                {STATE_LABEL[data.state]}
              </span>
              <span className="inline-block rounded-full bg-background-100 px-2.5 py-0.5 text-xs font-medium text-foreground-500">
                {data.reveal_mode === 0 ? "즉시공개" : "종료 후 공개"}
              </span>
              {data.is_joined === 1 && (
                <span className="inline-block rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-700">
                  참여완료
                </span>
              )}
            </div>
            <h1 className="mt-3 font-heading text-xl md:text-2xl font-bold text-foreground-950">
              {data.subject || data.title || "사다리게임"}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">{data.content}</p>
          </div>

          {/* 카운트다운 / 기간 */}
          <div className="shrink-0 rounded-xl border border-background-200 bg-background-50 px-4 py-3">
            <p className="text-xs text-foreground-500">
              {formatDateTime(data.start_date)} ~ {formatDateTime(data.end_date)}
            </p>
            {isOpen ? (
              <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-foreground-800">
                <span className="w-2 h-2 rounded-full bg-accent-500" aria-hidden="true"></span>
                종료까지{" "}
                <span className="font-heading tabular-nums text-accent-700">
                  {formatCountdown(endRemaining)}
                </span>
              </p>
            ) : data.state === "ready" ? (
              <p className="mt-1 text-sm font-semibold text-foreground-600">
                시작까지 {formatCountdown(startRemaining)}
              </p>
            ) : (
              <p className="mt-1 text-sm font-semibold text-foreground-500">종료된 게임</p>
            )}
          </div>
        </div>

        {/* 참여 현황 요약 */}
        <div className="mt-6 flex flex-wrap items-center gap-4 rounded-xl border border-background-200 bg-background-100/60 px-4 py-3 text-sm text-foreground-600">
          <span>
            참여 <strong className="text-foreground-900">{data.join_cnt}</strong>
            <span className="text-foreground-400"> / {data.slot_cnt}</span>
          </span>
          <span>
            남은 칸 <strong className="text-foreground-900">{data.left_cnt}</strong>
          </span>
          {data.my && (
            <span className="ml-auto text-foreground-700">
              내 칸 <strong className="text-primary-700">{data.my.slot + 1}번</strong>
              {data.my.join_date && (
                <span className="ml-1 text-xs text-foreground-400">
                  ({formatDateTime(data.my.join_date)})
                </span>
              )}
            </span>
          )}
        </div>

        {/* 안내 문구 */}
        {!isOpen && data.state === "ready" && (
          <p className="mt-4 text-center text-sm text-foreground-500">
            시작 전입니다. 시작 시각이 되면 출발칸을 골라 참여할 수 있습니다.
          </p>
        )}
        {isOpen && mySlot == null && (
          <p className="mt-4 text-center text-sm text-foreground-600">
            <i className="ri-cursor-line mr-1 text-accent-600" aria-hidden="true"></i>
            출발칸(세로줄)을 하나 눌러 참여하세요. 1인 1칸, 선택 후 변경할 수 없습니다.
          </p>
        )}
        {isOpen && mySlot != null && (
          <p className="mt-4 text-center text-sm text-foreground-600">
            <strong className="text-primary-700">{mySlot + 1}번 칸</strong>에 참여하셨습니다.
          </p>
        )}

        {/* 보드 */}
        <div className="mt-5 rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
          <LadderBoard
            slotCnt={data.slot_cnt}
            rungs={activeRungs}
            reveal={activeReveal}
            prizes={data.prizes ?? []}
            ends={activeEnds}
            taken={taken}
            mySlot={mySlot}
            clickable={isOpen}
            onSlotClick={handleSlotClick}
            animateSlot={animateSlot}
            onAnimateEnd={handleAnimateEnd}
          />
        </div>

        {/* 경품 안내 */}
        {(data.prizes?.length ?? 0) > 0 && (
          <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">경품 안내</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {data.prizes.map((p) => (
                <div
                  key={p.no}
                  className="flex items-center gap-2.5 rounded-lg border border-background-200 bg-background-100/60 px-3.5 py-2"
                >
                  <span
                    className="h-4 w-4 rounded-full"
                    style={{ backgroundColor: p.color }}
                    aria-hidden="true"
                  ></span>
                  <span className="text-sm font-semibold text-foreground-800">{p.name}</span>
                  <span className="text-xs text-foreground-500">× {p.qty}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 참여 현황 */}
        {joinedSlots.length > 0 && (
          <div className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              참여 현황 ({joinedSlots.length})
            </h2>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {joinedSlots.map((s) => (
                <li
                  key={s.slot}
                  className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 ${
                    s.is_mine === 1
                      ? "border-accent-300 bg-accent-50"
                      : "border-background-200 bg-background-50"
                  }`}
                >
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-xs font-bold text-foreground-600">
                    {s.slot + 1}
                  </span>
                  <Avatar name={s.name || "회원"} size={28} />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground-800">
                    {[s.name, s.aho].filter(Boolean).join(" ") || "회원"}
                  </span>
                  {s.is_mine === 1 && (
                    <span className="shrink-0 text-xs font-semibold text-primary-700">내 칸</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 목록으로 */}
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/ladder")}
            className="whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            목록으로
          </button>
        </div>
      </section>

      {/* 칸 선택 확인 모달 */}
      <Modal open={confirmSlot != null} onClose={() => setConfirmSlot(null)}>
        <div className="p-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-question-line text-xl" aria-hidden="true"></i>
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              {confirmSlot != null ? `${confirmSlot + 1}번 칸을 선택하시겠습니까?` : ""}
            </h2>
          </div>
          <p className="mt-3 text-sm text-foreground-600">
            한 번 고르면 바꿀 수 없습니다.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={joining}
              className="flex-1 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-3 text-sm font-bold text-primary-950 hover:bg-secondary-600 disabled:opacity-60 transition-colors"
            >
              {joining ? "참여 중…" : "참여하기"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmSlot(null)}
              disabled={joining}
              className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
            >
              취소
            </button>
          </div>
        </div>
      </Modal>

      {/* 결과 모달 */}
      <ResultModal
        open={result != null}
        prize={result?.prize ?? null}
        end={result?.end}
        onClose={() => setResult(null)}
      />
    </>
  );
}